// Doc-Ref: docs/features/collaborations-current.md
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  getFavoriteInstitution,
  getFavoriteUnit,
  getFavoriteFaculty,
} from "Explore/Services/Favorite";

/**
 * Fetch favorite institutions, units, and researchers (faculty) concurrently.
 */
const fetchFavorites = async () => {
  const [instRes, unitRes, facultyRes] = await Promise.allSettled([
    getFavoriteInstitution({ page: 0, rowsPerPage: 200 }),
    getFavoriteUnit({ page: 0, rowsPerPage: 200 }),
    getFavoriteFaculty({ page: 0, rowsPerPage: 200 }),
  ]);

  const extractList = (settledRes) => {
    if (settledRes.status !== "fulfilled" || !settledRes.value) return [];
    const val = settledRes.value;
    if (Array.isArray(val.results)) return val.results;
    if (Array.isArray(val)) return val;
    return [];
  };

  const institutions = extractList(instRes).map((item) => ({
    ...item,
    id: item.id,
    name: item.name,
    type: "institution",
    photo:
      item.photo_url ||
      item.photo ||
      item.logo_url ||
      (item.id
        ? `https://media.academic.online/institutions_images/${item.id}/seal.png`
        : null),
    avatar:
      item.photo_url ||
      item.photo ||
      item.logo_url ||
      (item.id
        ? `https://media.academic.online/institutions_images/${item.id}/seal.png`
        : null),
  }));

  const units = extractList(unitRes).map((item) => ({
    ...item,
    id: item.id,
    name: item.name,
    type: "unit",
    institution_id: item.institution_id,
    institution_name: item.institution_name,
    photo: item.institution_id
      ? `https://media.academic.online/institutions_images/${item.institution_id}/landscape.png`
      : null,
  }));

  const researchers = extractList(facultyRes).map((item) => ({
    ...item,
    id: item.id,
    name: item.name,
    type: "researcher",
    institution_name: item.institution_name,
    position: item.position,
    photo: item.photo_url || item.photo,
    avatar: item.photo_url || item.photo,
  }));

  return { institutions, units, researchers };
};

/**
 * Custom hook to fetch and cache user's favorite entities for Collaborations search suggestions.
 */
const useFavoriteEntities = () => {
  const user = useSelector((state) => state.user?.data);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["collab-favorite-entities", user?.id || user?.email],
    queryFn: fetchFavorites,
    enabled: !!user,
    staleTime: 60 * 1000, // 60s cache
  });

  const favorites = data || { institutions: [], units: [], researchers: [] };
  const hasFavorites =
    favorites.institutions.length > 0 ||
    favorites.units.length > 0 ||
    favorites.researchers.length > 0;

  return {
    favorites,
    isLoading: !!user && isLoading,
    hasFavorites,
    refetch,
    isLoggedIn: !!user,
  };
};

export default useFavoriteEntities;
